import { useAppDispatch, useAppSelector } from '@common-reducers/hooks/store.hook';
import { Snooze, labelActions } from '@common-reducers/labels-reducer';
import { _labelSelectOneObjectByQuery } from "@common-reducers/DBServiceThunks";
import { toggleChatLabelThunk } from '@common-reducers/labels-reducer/labels-thunks';
import { useState, useEffect } from 'react';
import moment from 'moment';
import { Loader2 } from 'lucide-react';
import { usePortalContainer } from '@/common/hooks/use-portal-container';
import { useTranslation } from "react-i18next";
import { namespace } from "@common-config/constants";

import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip";
import BTDatePicker from '@extension/context/content/components/bt-bulk-dashboard/time-and-date/bt-date-picker';

export default function CustomSnoozeModal() {
    const { t } = useTranslation(namespace);
    const dispatch = useAppDispatch()
    const customSnoozeWaContactId = useAppSelector((state) => state.LabelReducer.customSnoozeWaContactId)
    const labelNetWorkStatus = useAppSelector((state) => state.LabelReducer.labelNetWorkStatus)
    const snoozedLabel = useAppSelector(state => _labelSelectOneObjectByQuery(state.DBLabelReducer, {
        name: 'handle later'
    }))
    const loadingState = useAppSelector((state) => state.LabelReducer.loadingState)
    const portalContainer = usePortalContainer();

    // State for inputs
    const [dateValue, setDateValue] = useState<string>(''); // dd/mm/yyyy
    const [timeValue, setTimeValue] = useState<string>(''); // HH:mm
    const [isErr, setErr] = useState<boolean>(false)

    // Initialize/Reset when modal opens
    useEffect(() => {
        if (customSnoozeWaContactId) {
            // Default to tomorrow 9am if opening
            const tomorrow = moment().add(1, 'day');
            setDateValue(tomorrow.format('DD/MM/YYYY'));
            setTimeValue('09:00');
            setErr(false);
        }
    }, [customSnoozeWaContactId]);

    const handleClose = () => {
        dispatch(labelActions.setCustomSnoozeWaContactId(''))
    }

    async function handleSnooze() {
        if (!dateValue || !timeValue) return;

        // Parse date and time
        const futureDate = moment(`${dateValue} ${timeValue}`, 'DD/MM/YYYY HH:mm');
        const currentTime = moment();

        if (!futureDate.isValid()) {
            setErr(true);
            return;
        }

        if (futureDate.isBefore(currentTime)) {
            // date passed
            setErr(true);
        } else {
            setErr(false);
            const res = await dispatch(toggleChatLabelThunk({
                labelId: snoozedLabel?._id ?? '',
                waChatId: customSnoozeWaContactId,
                snooze: {
                    expiryDate: futureDate.toISOString()
                }
            }))

            if (res.payload) {
                setDateValue('');
                setTimeValue('');
                handleClose();
            }
        }
    }

    const isLoading = loadingState.includes(`${customSnoozeWaContactId}-${snoozedLabel?.name}`);
    const isOpen = Boolean(customSnoozeWaContactId);

    return (
        <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
            {/* Render into portal to ensure it stays on top of WhatsApp UI */}
            {portalContainer && (
                <DialogContent 
                    className="sm:max-w-[425px] flex flex-col gap-0 p-0 overflow-hidden bg-[var(--color-surface)] dark:bg-gray-900 border-gray-200 dark:border-gray-800"
                    container={portalContainer}
                >
                    <DialogHeader className="p-6 pb-2">
                        <DialogTitle>{t('labels.customHandleLater')}</DialogTitle>
                    </DialogHeader>

                    <div className="grid gap-4 py-4 px-6">
                        <div className="grid grid-cols-4 items-center gap-4">
                            <Label htmlFor="date" className="text-right">
                                {t('labels.date')}
                            </Label>
                            <div className="col-span-3">
                                <BTDatePicker
                                    value={dateValue}
                                    onChange={(val) => setDateValue(val)}
                                    primaryColor="#3b82f6"
                                />
                            </div>
                        </div>
                        <div className="grid grid-cols-4 items-center gap-4">
                            <Label htmlFor="time" className="text-right">
                                {t('labels.time')}
                            </Label>
                            <Input
                                id="time"
                                type="time"
                                value={timeValue}
                                onChange={(e) => {
                                    setTimeValue(e.target.value);
                                    setErr(false);
                                }}
                                className={`col-span-3 ${isErr ? 'border-red-500 focus-visible:ring-red-500' : ''}`}
                            />
                        </div>
                        {isErr && (
                            <div className="text-xs text-red-500 text-right">
                                {t('labels.invalidFutureDateTime')}
                            </div>
                        )}
                    </div>

                    <DialogFooter className="p-6 pt-2 flex flex-col sm:flex-row gap-2 justify-end">
                        <Button variant="outline" onClick={handleClose}>
                            {t('common.cancel')}
                        </Button>
                        <TooltipProvider>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <span tabIndex={0} className="inline-block"> 
                                        <Button
                                            onClick={handleSnooze}
                                            disabled={!dateValue || !timeValue || isLoading}
                                        >
                                            {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                                            {t('labels.handleLaterAction')}
                                        </Button>
                                    </span>
                                </TooltipTrigger>
                                {(isLoading || !dateValue || !timeValue) && (
                                    <TooltipContent>
                                        <p>
                                            {isLoading ? t('labels.settingHandleLater') :
                                             !dateValue ? t('labels.pleaseSelectDate') :
                                             !timeValue ? t('labels.pleaseSelectTime') : ""}
                                        </p>
                                    </TooltipContent>
                                )}
                            </Tooltip>
                        </TooltipProvider>
                    </DialogFooter>
                </DialogContent>
            )}
        </Dialog>
    );
}